// Generate Report — the Executive Summary results as an Excel workbook.
//
// One row per campaign-stage, flattened so the sheet is filterable: the
// campaign identity repeats on each of its stage rows and a Campaigns column
// says which section (Ongoing / Past) the block came from. Styling follows the
// same brand palette the project tabs' report already uses.

// xlsx-js-style is a drop-in fork of SheetJS that ALSO applies cell styles on
// write — the plain `xlsx` build ignores them.
import XLSX from 'xlsx-js-style';
import moment from 'moment';

import { COUNT_DASH, formatCount, formatPercent } from './formatters';

const BRAND = '3B2A4D';
const LABEL_FILL = 'F0EEF4';
const ZEBRA_FILL = 'F7F7FA';
const thin = { style: 'thin', color: { rgb: 'D9D9D9' } };
const allBorders = { top: thin, bottom: thin, left: thin, right: thin };

const COLUMNS = [
  'Campaigns',
  'CMO',
  'Site / MFG Plant',
  'Project ID',
  'Campaign ID',
  'Campaign Status',
  'Stage',
  'Planned Batches',
  'Completed Batches',
  'Ongoing Batches',
  'Completion (%)',
];

const blockRows = (block, sectionLabel) =>
  (block.stages || []).map((stage) => [
    sectionLabel,
    block.cmoName || '',
    block.siteMfgPlant || '',
    block.projectId || '',
    block.campaignId || '',
    block.campaignStatus || '',
    stage.stageName || '',
    formatCount(stage.plannedBatches),
    formatCount(stage.completedBatches),
    formatCount(stage.ongoingBatches),
    formatPercent(stage.completionPercent),
  ]);

/**
 * Writes the workbook straight to the user's downloads.
 *
 * @param {{ongoing: Array, past: Array, lastSyncedAt: ?string}} results
 * @param {{filters: string[]}} meta  filter summary lines for the header block
 */
export const downloadExecutiveSummaryReport = (results, meta = {}) => {
  const rows = [
    ...(results.ongoing || []).flatMap((b) => blockRows(b, 'Ongoing')),
    ...(results.past || []).flatMap((b) => blockRows(b, 'Past')),
  ];
  // Nothing to write is not an error — the button is disabled without results,
  // so this only guards a race with a Refresh that emptied them.
  if (!rows.length) return false;

  const syncedAt = results.lastSyncedAt
    ? moment(results.lastSyncedAt).format('DD-MMM-YYYY HH:mm:ss').toUpperCase()
    : COUNT_DASH;
  const headerRows = [
    ['CMO', meta.cmo || 'All'],
    ['Site / MFG Plant', meta.site || 'All'],
    ['Project ID', meta.project || 'All'],
    ['Last Synced', syncedAt],
    ['Generated On', moment().format('DD-MMM-YYYY HH:mm:ss').toUpperCase()],
  ];

  const aoa = [['Executive Summary'], []];
  const metaStartRow = aoa.length;
  headerRows.forEach((r) => aoa.push(r));
  aoa.push([]);
  const tableHeaderRow = aoa.length;
  aoa.push(COLUMNS);
  rows.forEach((r) => aoa.push(r));

  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  const colCount = COLUMNS.length;

  worksheet['!cols'] = COLUMNS.map((header, c) => {
    let maxLen = header.length;
    rows.forEach((row) => {
      const len = row[c] == null ? 0 : String(row[c]).length;
      if (len > maxLen) maxLen = len;
    });
    return { wch: Math.min(Math.max(maxLen + 2, 10), 30) };
  });

  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
    ...headerRows.map((_, i) => ({
      s: { r: metaStartRow + i, c: 1 },
      e: { r: metaStartRow + i, c: colCount - 1 },
    })),
  ];

  const setStyle = (r, c, style) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
    if (cell) cell.s = { ...(cell.s || {}), ...style };
  };

  setStyle(0, 0, {
    font: { bold: true, sz: 13, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: BRAND } },
    alignment: { horizontal: 'left', vertical: 'center' },
  });

  headerRows.forEach((_, i) => {
    const r = metaStartRow + i;
    setStyle(r, 0, {
      font: { bold: true, color: { rgb: BRAND } },
      fill: { fgColor: { rgb: LABEL_FILL } },
      alignment: { vertical: 'top', wrapText: true },
      border: allBorders,
    });
    setStyle(r, 1, { alignment: { vertical: 'top', wrapText: true }, border: allBorders });
  });

  for (let c = 0; c < colCount; c += 1) {
    setStyle(tableHeaderRow, c, {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { fgColor: { rgb: BRAND } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: allBorders,
    });
  }

  rows.forEach((_, i) => {
    const r = tableHeaderRow + 1 + i;
    for (let c = 0; c < colCount; c += 1) {
      setStyle(r, c, {
        alignment: { vertical: 'top', wrapText: true },
        border: allBorders,
        ...(i % 2 === 1 ? { fill: { fgColor: { rgb: ZEBRA_FILL } } } : {}),
      });
    }
  });

  const rowHeights = [];
  rowHeights[0] = { hpt: 22 };
  rowHeights[tableHeaderRow] = { hpt: 20 };
  worksheet['!rows'] = rowHeights;

  worksheet['!freeze'] = {
    xSplit: 0,
    ySplit: tableHeaderRow + 1,
    topLeftCell: XLSX.utils.encode_cell({ r: tableHeaderRow + 1, c: 0 }),
    activePane: 'bottomLeft',
    state: 'frozen',
  };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Executive Summary');
  XLSX.writeFile(workbook, `Executive_Summary_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  return true;
};
